/*
 * Age-verification gate.
 *
 * Pure decision logic is exported via module.exports and unit-tested under
 * Node (tests/age-verification.test.js); the DOM wiring is skipped when
 * `window` is absent, so the shipped file is the single source of truth.
 *
 * On the storefront: read the per-customer server flag from
 * <meta name="br-compliance-required"> (only set for flagged, logged-in
 * customers), combine it with a remembered acceptance in localStorage, and
 * reveal #br-age-verification when the customer still needs to confirm.
 * The gate cannot be dismissed without answering (no Esc / no backdrop close).
 */
(function (global) {
  'use strict';

  var STORAGE_KEY = 'br-age-verification';
  var CURRENT_VERSION = 1;
  var DAY_MS = 86400000;

  // "1" (from the Liquid flag) -> true. Anything else -> false.
  function parseFlag(content) {
    return String(content == null ? '' : content).trim() === '1';
  }

  // Decide whether to show the gate. Pure.
  function shouldShow(opts) {
    opts = opts || {};
    return Boolean(opts.complianceRequired) && !opts.accepted;
  }

  // Parse a stored acceptance record; null on anything malformed.
  function readAcceptance(raw) {
    if (!raw) return null;
    try {
      var o = JSON.parse(raw);
      return o && typeof o === 'object' ? o : null;
    } catch (e) {
      return null;
    }
  }

  // Is a stored acceptance still valid? Wrong version, missing/!invalid
  // timestamp, or older than ttlDays (when ttlDays > 0) -> false.
  function isAcceptanceValid(record, nowMs, ttlDays) {
    if (!record || record.v !== CURRENT_VERSION || !record.at) return false;
    var atMs = Date.parse(record.at);
    if (isNaN(atMs)) return false;
    if (atMs > nowMs + DAY_MS) return false; // clock-skew / tampered future date
    if (!ttlDays || ttlDays <= 0) return false; // 0 = never remember (ask every visit)
    if (nowMs - atMs > ttlDays * DAY_MS) return false; // expired
    return true;
  }

  function buildAcceptance(nowIso) {
    return { v: CURRENT_VERSION, at: nowIso };
  }

  function parseTtlDays(raw) {
    var n = parseInt(raw, 10);
    return isNaN(n) || n < 0 ? 0 : n;
  }

  // Where to send a customer who declines. Null = stay blocked (no redirect).
  function declineTarget(opts) {
    opts = opts || {};
    if (!opts.logout) return null;
    var url = String(opts.redirect == null ? '' : opts.redirect).trim();
    return url || null;
  }

  var api = {
    STORAGE_KEY: STORAGE_KEY,
    CURRENT_VERSION: CURRENT_VERSION,
    parseFlag: parseFlag,
    shouldShow: shouldShow,
    readAcceptance: readAcceptance,
    isAcceptanceValid: isAcceptanceValid,
    buildAcceptance: buildAcceptance,
    parseTtlDays: parseTtlDays,
    declineTarget: declineTarget,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  // ── Below: DOM wiring only. Skipped under Node (no window). ──────────
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  global.BRAgeVerification = api;

  function metaFlag() {
    var el = document.querySelector('meta[name="br-compliance-required"]');
    return parseFlag(el && el.getAttribute('content'));
  }

  function storedAccepted(ttlDays) {
    var raw = null;
    try { raw = window.localStorage.getItem(STORAGE_KEY); } catch (e) { raw = null; }
    return isAcceptanceValid(readAcceptance(raw), Date.now(), ttlDays);
  }

  function persistAcceptance() {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(buildAcceptance(new Date().toISOString())));
    } catch (e) { /* private mode: gate simply re-shows next load */ }
  }

  function lockScroll(lock) {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  function trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    var focusable = container.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function init() {
    var gate = document.getElementById('br-age-verification');
    if (!gate) return;

    // Never block the Shopify theme editor / design mode.
    if (global.Shopify && global.Shopify.designMode) return;

    var ttlDays = parseTtlDays(gate.getAttribute('data-ttl-days'));

    if (!shouldShow({ complianceRequired: metaFlag(), accepted: storedAccepted(ttlDays) })) {
      return; // not required, or already confirmed recently
    }

    var card = gate.querySelector('.br-age-gate__card');
    var confirmBtn = gate.querySelector('[data-br-age-gate-confirm]');
    var declineBtn = gate.querySelector('[data-br-age-gate-decline]');
    var askStep = gate.querySelector('[data-br-age-gate-step="ask"]');
    var declinedStep = gate.querySelector('[data-br-age-gate-step="declined"]');
    var lastFocus = document.activeElement;

    gate.removeAttribute('hidden');
    lockScroll(true);
    // Focus the dialog (not a button) so no button gets a focus ring on load;
    // keyboard users still get a clean ring when they Tab to the buttons.
    if (card) { try { card.focus({ preventScroll: true }); } catch (e) { card.focus(); } }

    if (confirmBtn) {
      confirmBtn.addEventListener('click', function () {
        persistAcceptance();
        gate.setAttribute('hidden', '');
        lockScroll(false);
        try { if (lastFocus && lastFocus.focus) lastFocus.focus(); } catch (e) { /* noop */ }
      });
    }

    if (declineBtn) {
      declineBtn.addEventListener('click', function () {
        if (askStep) askStep.setAttribute('hidden', '');
        if (declinedStep) declinedStep.removeAttribute('hidden');
        // A declined customer is signed out (no session keeps an under-age
        // declaration), unless the merchant turned that off — then stay locked.
        var target = declineTarget({
          logout: String(gate.getAttribute('data-decline-logout')) === 'true',
          redirect: gate.getAttribute('data-decline-redirect'),
        });
        if (target) {
          var note = gate.querySelector('[data-br-age-gate-signout]');
          if (note) note.removeAttribute('hidden');
          global.setTimeout(function () {
            try { window.location.href = target; } catch (e) { /* noop */ }
          }, 1800);
        }
      });
    }

    // Block Esc; keep focus inside the gate.
    gate.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); return; }
      if (card) trapFocus(card, e);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(typeof window !== 'undefined' ? window : globalThis);
